import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../../my-app/src/environments/environment';
import { filter } from 'rxjs';
import { MacroTemplateService } from '../../shared-services/macro-template.service';
import { ChatTabContextService } from '../../shared-services/chat-tab-context.service';

@Component({
  selector: 'app-side-navbar',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatTooltipModule,
    RouterModule,
  ],
  templateUrl: './side-navbar.component.html',
  styleUrl: './side-navbar.component.scss',
})
export class SideNavbarComponent {
  private readonly previousRouteStorageKey = 'codex.previous-route';
  @ViewChild('macroToggleButton') macroToggleButton?: ElementRef<HTMLElement>;
  @ViewChild('macroPanelElement') macroPanelElement?: ElementRef<HTMLElement>;

  /* ===== EXISTING STATE (UNCHANGED) ===== */
  isExpanded = true;
  showSubmenu = false;
  isShowing = false;
  showSubSubMenu = false;
  isCollapsed = true;
  windowWidth = window.innerWidth;

  username!: string;
  logo = environment.COMPANY_LOGO_SRC;
  companyText = environment.COMPANY_LOGO_TEXT;
  secondaryLogo = environment.SECONDARY_LOGO_SRC;

  menuItems: any[] = [];
  isChatRoute = false;
  activeChatTab: 'help' | 'chat' = 'help';
  isMacroPanelOpen = false;
  isLoadingMacros = false;
  isLoadingMacroContent = false;
  macroErrorMessage = '';
  macros: string[] = [];
  currentDashboardTitle = '';

  private readonly wrapperApiBaseUrl = (environment.agentApiUrl || '').trim().replace(/\/$/, '');
  private dashboardTitleCache = new Map<string, string>();

  /* ===== CHILD MENU STATE (ONLY ADDITION) ===== */
  expandedMenus: Set<string> = new Set();

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService,
    private router: Router,
    private macroTemplateService: MacroTemplateService,
    private chatTabContext: ChatTabContextService
  ) {
    this.chatTabContext.activeTab$.subscribe((tab) => {
      this.activeChatTab = tab;
      if (tab !== 'chat') {
        this.isMacroPanelOpen = false;
      }
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.persistPreviousRoute(event.urlAfterRedirects || '');
        this.isChatRoute = event.urlAfterRedirects.includes('/chat');
        this.activeRoute = event.urlAfterRedirects || '';
        this.updateDashboardTitle();
        if (!this.isChatRoute) {
          this.isMacroPanelOpen = false;
        }
      });
  }

  // Track active route for highlighting
  activeRoute: string = '';

  get welcomeUsername(): string {
    const cleanedUsername = String(this.username || '')
      .trim()
      .replace(/[.!?,\s]+$/, '');
    return cleanedUsername || 'User';
  }

  /* ===== INIT ===== */
  async ngOnInit(): Promise<void> {
    const isLoggedIn = await this.keycloakService.isLoggedIn();
    if (isLoggedIn) {
      const userDetails = await this.keycloakService.loadUserProfile();
      this.username =
        `${userDetails?.firstName ?? ''} ${userDetails?.lastName ?? ''}`.trim() || 'User';
    }
    this.isChatRoute = (this.router.url || '').includes('/chat');
    this.updateDashboardTitle();
    this.loadSidenavConfig();
  }

  navigateTo(){
    this.router.navigate(['/landing']);
  }

  /* ===== AUTH ===== */
  async logout(): Promise<void> {
    await this.keycloakService.logout(window.location.origin);
    localStorage.clear();
  }

  /* ===== MENU CONFIG ===== */
  loadSidenavConfig(): void {
    this.http
      .get<any[]>('assets/side-navbar-config/sidenav.config.json')
      .subscribe((data) => {
        this.menuItems = data || [];
      });
  }

  private updateDashboardTitle(): void {
    const configKey = this.getCurrentDashboardConfigKey();
    if (!configKey) {
      this.currentDashboardTitle = '';
      return;
    }

    if (this.dashboardTitleCache.has(configKey)) {
      this.currentDashboardTitle = this.dashboardTitleCache.get(configKey) || '';
      return;
    }

    this.http.get<any>(`assets/dashboard-config/${configKey}`).subscribe({
      next: (config) => {
        const title =
          typeof config?.['dashboard-name'] === 'string'
            ? config['dashboard-name'].trim()
            : '';
        this.dashboardTitleCache.set(configKey, title);
        if (this.getCurrentDashboardConfigKey() === configKey) {
          this.currentDashboardTitle = title;
        }
      },
      error: () => {
        this.dashboardTitleCache.set(configKey, '');
        if (this.getCurrentDashboardConfigKey() === configKey) {
          this.currentDashboardTitle = '';
        }
      },
    });
  }

  private getCurrentDashboardConfigKey(): string {
    const leaf = this.getDeepestSnapshot(this.router.routerState.snapshot.root);
    const configFile = leaf?.data?.['configFile'];
    const workflowFile = leaf?.data?.['dvmWorkflowAppId'];
    return String(configFile || workflowFile || '').trim();
  }

  private getDeepestSnapshot(snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let node = snapshot;
    while (node.firstChild) {
      node = node.firstChild;
    }
    return node;
  }

  /* ===== SIDEBAR ===== */
  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  mouseenter(): void {
    if (!this.isExpanded) {
      this.isShowing = true;
    }
  }

  mouseleave(): void {
    if (!this.isExpanded) {
      this.isShowing = false;
    }
  }

  /* ===== EXTERNAL LINKS ===== */
  goToHelp(): void {
    const helpUrl = environment.HELP_URL || 'https://default.help.com';
    window.open(helpUrl, '_blank');
  }

  goToFeedback(): void {
    const feedbackUrl = environment.FEEDBACK_URL;
    if (feedbackUrl) {
      window.open(feedbackUrl, '_blank');
    }
  }

  showNotifications(): void {
    // Notifications action intentionally silent to avoid disruptive alerts
    // Use console.debug for developer visibility
    console.debug('Notifications clicked');
  }

  /* ==================================================
     CHILD / SUBMENU LOGIC (INTEGRATED ONLY)
     ================================================== */

  hasChildren(item: any): boolean {
    return Array.isArray(item?.children) && item.children.length > 0;
  }

  toggleSubmenu(menuName: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.expandedMenus.has(menuName)) {
      this.expandedMenus.delete(menuName);
    } else {
      this.expandedMenus.add(menuName);
    }
  }

  isMenuExpanded(menuName: string): boolean {
    return this.expandedMenus.has(menuName);
  }

  isActive(item: any): boolean {
    if (!item) return false;
    const route = this.activeRoute || this.router.url || '';
    // Normalize paths to compare
    const itemPath = (item.path || '').startsWith('/') ? item.path : '/' + (item.path || '');
    return route === itemPath || route.startsWith(itemPath) || route.includes(itemPath);
  }

  toggleMacroPanel(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.isMacroPanelOpen) {
      this.loadMacros();
    }
    this.isMacroPanelOpen = !this.isMacroPanelOpen;
  }

  loadMacros(): void {
    this.macroErrorMessage = '';
    if (!this.wrapperApiBaseUrl) {
      this.macroErrorMessage = 'Wrapper API URL is not configured.';
      return;
    }

    this.isLoadingMacros = true;
    this.http.get<{ macros?: string[] }>(`${this.wrapperApiBaseUrl}/macros`).subscribe({
      next: (payload) => {
        this.macros = Array.isArray(payload?.macros) ? payload.macros : [];
        this.isLoadingMacros = false;
      },
      error: (error) => {
        const message = error?.error?.message || error?.message || 'Failed to load macros.';
        this.macroErrorMessage = String(message);
        this.macros = [];
        this.isLoadingMacros = false;
      }
    });
  }

  canShowMacroShortcut(): boolean {
    return this.isChatRoute && this.activeChatTab === 'chat';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isMacroPanelOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (!target) {
      return;
    }

    const clickedToggle = this.macroToggleButton?.nativeElement?.contains(target) ?? false;
    const clickedPanel = this.macroPanelElement?.nativeElement?.contains(target) ?? false;

    if (!clickedToggle && !clickedPanel) {
      this.isMacroPanelOpen = false;
    }
  }

  selectMacro(macroName: string): void {
    this.macroErrorMessage = '';
    if (!macroName || !this.wrapperApiBaseUrl) {
      return;
    }

    this.isLoadingMacroContent = true;
    this.http.get<{ name?: string; content?: string }>(`${this.wrapperApiBaseUrl}/macros/content`, {
      params: { name: macroName }
    }).subscribe({
      next: (payload) => {
        const content = payload?.content || '';
        if (!content.trim()) {
          this.macroErrorMessage = `Macro "${macroName}" is empty.`;
        } else {
          this.macroTemplateService.pushTemplate(content);
          this.isMacroPanelOpen = false;
        }
        this.isLoadingMacroContent = false;
      },
      error: (error) => {
        const message = error?.error?.message || error?.message || `Failed to load macro "${macroName}".`;
        this.macroErrorMessage = String(message);
        this.isLoadingMacroContent = false;
      }
    });
  }

  private persistPreviousRoute(nextRoute: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    const normalizedNextRoute = this.normalizeRoutePath(nextRoute);
    if (!normalizedNextRoute) {
      return;
    }

    const currentRoute = this.normalizeRoutePath(this.activeRoute || this.router.url || '');
    if (currentRoute && currentRoute !== normalizedNextRoute) {
      sessionStorage.setItem(this.previousRouteStorageKey, currentRoute);
    }
  }

  private normalizeRoutePath(route: string): string {
    const rawRoute = String(route || '').split('?')[0].split('#')[0].trim();
    if (!rawRoute) {
      return '';
    }

    return rawRoute.startsWith('/') ? rawRoute : `/${rawRoute}`;
  }
}
